const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '');
  const intValue = parseInt(normalized, 16);
  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255,
  };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b)
    .toString(16)
    .padStart(2, '0')}`.toUpperCase();

export const normalizeHexColor = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const hex = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  return hex.toUpperCase();
};

export const getLuminance = (hex: string) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return 0;
  const { r, g, b } = hexToRgb(normalized);
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastRatio = (lumA: number, lumB: number) => {
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
};

export const prefersLightText = (hex: string) => {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return false;
  const luminance = getLuminance(normalized);
  const contrastWithWhite = getContrastRatio(luminance, 1);
  const contrastWithBlack = getContrastRatio(luminance, 0);
  return contrastWithWhite >= contrastWithBlack;
};

export const isDarkHex = (hex: string) => prefersLightText(hex);

export const mixHexColors = (hexA: string, hexB: string, weight = 0.5) => {
  const base = hexToRgb(normalizeHexColor(hexA) || '#000000');
  const target = hexToRgb(normalizeHexColor(hexB) || '#000000');
  const ratio = Math.max(0, Math.min(1, weight));
  return rgbToHex(
    base.r + (target.r - base.r) * ratio,
    base.g + (target.g - base.g) * ratio,
    base.b + (target.b - base.b) * ratio,
  );
};

export const lightenHexColor = (hex: string, amount = 0.2) => mixHexColors(hex, '#FFFFFF', amount);
export const darkenHexColor = (hex: string, amount = 0.2) => mixHexColors(hex, '#000000', amount);
